import { RowSelectionState } from "@soft-db/ui";
import { computed, Ref } from "vue";

export type SingleSelect = {
	item: string;
	value: boolean;
};

export type MultiSelect = {
	items: string[];
	value: boolean;
};

export type TableSelectionOptions = {
	onChange: (select: MultiSelect) => void;
};

export function useTableSelections(
	selectionsSet: Ref<Set<string>>,
	options?: TableSelectionOptions
) {
	function isSameSelection(newSelections: RowSelectionState) {
		const keys = Object.keys(newSelections);
		if (keys.length !== selectionsSet.value.size) return false;

		for (const key of keys) {
			if (!selectionsSet.value.has(key)) {
				return false;
			}
		}

		return true;
	}

	function signalSelectionsChanged(selections: RowSelectionState, previousSelection: Set<string>) {
		if (!selections) return;

		const newSelection = new Set(Object.keys(selections));

		const added = newSelection.size > previousSelection.size;
		const biggerSet = added ? newSelection : previousSelection;
		const smallerSet = added ? previousSelection : newSelection;

		const ids: string[] = [];

		for (const value of biggerSet) {
			if (!smallerSet.has(value)) {
				ids.push(value);
			}
		}

		options?.onChange({
			items: ids,
			value: added,
		});
	}

	const selections = computed({
		get: () => {
			return Array.from(selectionsSet.value).reduce<RowSelectionState>((selection, favorite) => {
				selection[favorite] = true;
				return selection;
			}, {});
		},
		set: (selection: RowSelectionState) => {
			if (isSameSelection(selection)) return;

			const previousSelection = selectionsSet.value;
			selectionsSet.value = new Set(Array.from(Object.keys(selection || {})));

			signalSelectionsChanged(selection, previousSelection);
		},
	});

	return { selections };
}
